import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { toast } from "sonner";

// Only allow `next` to be a same-origin relative path (must start with `/` and
// must not begin with `//` which is an open redirect). Anything else falls back
// to the default destination.
const sanitizeNext = (raw, fallback) => {
  if (!raw) return fallback;
  if (typeof raw !== "string") return fallback;
  if (!raw.startsWith("/") || raw.startsWith("//")) return fallback;
  return raw;
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = sanitizeNext(searchParams.get("next"), "/dashboard");
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      const dest = (u && u.role === "teacher") ? "/teacher/dashboard" : nextPath;
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo flex flex-col">
      <div className="container mx-auto py-6">
        <Link to="/"><Logo size={44} /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-mm-navy/10 rounded-2xl p-8 fade-up">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trailhead</div>
          <h1 className="font-heading text-3xl font-black text-mm-navy mt-2">Continue journey</h1>
          <p className="text-mm-navy/70 mt-2 text-sm">Pick up where you left off on the mountain.</p>

          <div className="mt-6" data-testid="login-google-block">
            <GoogleSignInButton label="signin_with" />
          </div>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <div className="flex-1 h-px bg-mm-navy/15" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-mm-steel">or use email</span>
            <div className="flex-1 h-px bg-mm-navy/15" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                required className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Password</label>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                required className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                data-testid="login-password-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-trail btn-gold w-full mt-2" data-testid="login-submit-btn">
              {loading ? "Unlocking trailhead..." : "Resume climb"}
            </button>
          </form>
          <div className="text-sm text-mm-navy/70 mt-4 text-center">
            <Link to="/forgot-password" className="font-bold text-mm-navy hover:text-mm-steel" data-testid="login-forgot-password-link">
              Forgot password?
            </Link>
          </div>
          <div className="text-sm text-mm-navy/70 mt-6 text-center">
            New to the trail?{" "}
            <Link to={`/signup${nextPath !== "/dashboard" ? `?next=${encodeURIComponent(nextPath)}` : ""}`} className="font-bold text-mm-navy hover:text-mm-steel" data-testid="login-to-signup-link">Start your expedition</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
